import {
  getInstructionalLevel,
  remapGoalSubject, returnGoalDomainsForPLWSkill,
  returnGoalSubjectForPLWSubject,
  returnGradeFromEnrolledGrade,
  subjectHasGradeFiltersForAlignedGoals,
  reading,
} from '@/api/presentlevels'
import { getRelevantGoalsForText } from '@/api/pathways'
import { gradeOptions } from '@/components/strategies/strategy_filters_util'
import { wikiToPlainText } from '@/util/tokenize_wiki_text'
import {
  ACTION_TYPES,
  CONTENT_TYPES,
  actionPermissions,
  blurContentPermissions,
} from '@/util/access_visibility'
import { createStrategyShortcutObject } from '@/util/strategy_shortcuts'

export default {
  namespaced: true,
  state: {
    studentName: '',
    enrolledGrade: '',
    areasOfNeed: [],
    draftedSkills: {
      strengths: {},
      needs: {},
    },
    breadcrumbsInfo: {
      uuid: '',
      availableStages: [],
    },
    saving: false,

    showBlocker: false,
    blockerPermissions: null,

    lastUpdate: null,
    goalsShortcutData: {},
    strategiesShortcutData: {},
    resourceDocs: [],
    goalRecommendations: [],
    loadingRecommendations: false,
    recommendationsRetrieved: false,
    goalRecommendationsFailed: false,
    gradeForBrowse: '',
    hasAccess: undefined,
  },
  mutations: {
    resetState(state) {
      state.breadcrumbsInfo.uuid = ''
      state.breadcrumbsInfo.availableStages = []
      state.enrolledGrade = ''
      state.studentName = ''
      state.areasOfNeed = []
    },
    setShowBlocker(state, flag) {
      state.showBlocker = flag
    },

    // permissions should be an object returned from actionPermissions() in access_visibility.js
    setBlockerPermissions(state, permissions) {
      state.blockerPermissions = permissions
    },
    setBreadcrumbInfo(state, info) {
      state.breadcrumbsInfo.uuid = info.uuid
      state.breadcrumbsInfo.availableStages = info.availableStages
    },
    setSaving(state, saving) {
      state.saving = saving
    },
    setLastUpdate(state, lastUpdate) {
      state.lastUpdate = lastUpdate
    },
    setGoalShortcutData(state, data) {
      state.goalsShortcutData = data
    },
    setStrategyShorcutData(state, data) {
      state.strategiesShortcutData = data
    },
    setEnrolledGrade(state, enrolledGrade) {
      state.enrolledGrade = enrolledGrade
    },
    setStudentName(state, name) {
      state.studentName = name
    },
    setAreasOfNeed(state, areasOfNeed) {
      state.areasOfNeed = areasOfNeed
    },
    setResourceDocs(state, resourceDocs) {
      state.resourceDocs = resourceDocs
    },
    setGoalRecommendations(state, goals) {
      state.goalRecommendations = goals
    },
    setLoadingRecommendations(state, bool) {
      state.loadingRecommendations = bool
    },
    setRecommendationsRetrieved(state, bool) {
      state.recommendationsRetrieved = bool
    },
    setGoalRecommendationsFailed(state, bool) {
      state.goalRecommendationsFailed = bool
    },
    setGradeForBrowse(state, bool) {
      state.gradeForBrowse = bool
    },
    setDraftedSkillsFromStatements(state, statementsByArea) {
      const { strengths = {}, needs = {} } = statementsByArea
      for (let [subject, skills] of Object.entries(strengths)) {
        state.draftedSkills.strengths[subject] = skills.map(s => s.skill_name)
      }
      for (let [subject, skills] of Object.entries(needs)) {
        state.draftedSkills.needs[subject] = skills.map(s => s.skill_name)
      }
    },
    setHasAccess(state, hasAccess) {
      state.hasAccess = hasAccess
    }
  },
  getters: {
    enrolledGradeRangeOption(state) {
      let cleanedGrade = state.enrolledGrade.replace(/st|nd|rd|th/gi, '')
      for (let option of gradeOptions) {
        if (option.value.includes(cleanedGrade)) {
          return option
        }
      }
      return { label: state.enrolledGrade, value: [cleanedGrade] }
    },
    developPLWPermissions(state, getters, rootState, rootGetters) {
      return actionPermissions(ACTION_TYPES.DEVELOP_PLW, '', {}, rootGetters['user/authInfo'])
    },
    blurPLWPermissions(state, getters, rootState, rootGetters) {
      return blurContentPermissions(CONTENT_TYPES.PRESENTLEVEL, {}, rootGetters['user/authInfo'])
    },
    draftedStrengths(state) {
      return state.draftedSkills.strengths
    }
  },
  actions: {
    generateGoalShortcutData(
      { state, rootGetters, commit },
      { categoryMapping, name, subjects, skillAreas, skillAreaGrades, enrolledGrade }
    ) {
      let publishedCategories = rootGetters['contentProfile/publishedSubjects'].reduce(
        (categoryArr, subject) => {
          return categoryArr.concat(subject.categories)
        },
        []
      )

      let mappings = Object.keys(categoryMapping).reduce((mapping, currentKey) => {
        if (categoryMapping[currentKey]) {
          mapping[currentKey] = categoryMapping[currentKey].split(',')
        }
        return mapping
      }, {})

      let filteredMappings = {}
      for (let skillName in mappings) {
        filteredMappings[skillName] = mappings[skillName].filter((category) => {
          return publishedCategories.includes(category)
        })
      }

      let shortcutTemplate = {
        name: name,
        subject: '',
        category: '',
        grades: enrolledGrade.replace(/st|nd|rd|th/gi, ''),
        instructionalLevel: '',
      }

      let shortcuts = {}
      subjects.forEach((subject) => {
        let subjectSkills = skillAreas[subject]
        subjectSkills.forEach((skillName) => {
          let mappedCategories = filteredMappings[skillName]
          if (mappedCategories && mappedCategories.length > 0) {
            shortcuts[skillName] = mappedCategories.map((category) => {
              return Object.assign({}, shortcutTemplate, {
                subject: remapGoalSubject(subject),
                subjectDomain: category,
                instructionalLevel: getInstructionalLevel(
                  enrolledGrade,
                  skillAreaGrades[skillName]
                ),
              })
            })
          }
        })
      })

      return commit('setGoalShortcutData', shortcuts)
    },
    generateStrategyShortcutData(
      { commit, getters },
      { barrierMappings, subjects, skillAreas, skillAreaGrades, enrolledGrade }
    ) {
      let mappings = Object.keys(barrierMappings).reduce((mapping, currentKey) => {
        if (barrierMappings[currentKey]) {
          mapping[currentKey] = barrierMappings[currentKey].split(',')
        }
        return mapping
      }, {})

      let shortcuts = {}
      subjects.forEach((subject) => {
        let subjectSkills = skillAreas[subject]
        subjectSkills.forEach((skillName) => {
          let mappedBarriers = mappings[skillName]
          if (mappedBarriers && mappedBarriers.length > 0) {
            shortcuts[skillName] = mappedBarriers.map((barrier) => {
              return createStrategyShortcutObject(
                subject,
                barrier,
                getters.enrolledGradeRangeOption.value.join(',')
              )
            })
          }
        })
      })

      return commit('setStrategyShorcutData', shortcuts)
    },
    async retrieveRelevantGoals(context, { statementsByArea, grade }) {
      let gradeMapped = returnGradeFromEnrolledGrade(grade)
      context.commit('setGradeForBrowse', gradeMapped)

      // Need to create a set of queries, one for each statement
      const { querySet, resultSet } = buildGoalQueryAndResultSetForStatementsByArea(statementsByArea, gradeMapped)

      // Set this before sending the request so we can show outline of what will be retrieved to user
      context.commit('setGoalRecommendations', resultSet)

      // Don't proceed if request is already made
      if (!context.state.RecommendationsRetrieved) {
        context.commit('setLoadingRecommendations', true)
        try {
          let goalRecommendations = await getRelevantGoalsForText({ queries: querySet, size: 3 })
          goalRecommendations = resultSet.map((result, i) => {
            result.goals = goalRecommendations[i]
            return result
          })
          context.commit('setGoalRecommendations', goalRecommendations)
          context.commit('setLoadingRecommendations', false)
          context.commit('setRecommendationsRetrieved', true)
        } catch (error) {
          console.error('Error retrieving relevant goals: ', error)
          if (querySet.length) {
            context.commit('setGoalRecommendationsFailed', true)
          }
          context.commit('setGoalRecommendations', [])
          context.commit('setLoadingRecommendations', false)
          context.commit('setRecommendationsRetrieved', true)
        }
      }
    },
    setBreadcrumbInfo(context, info) {
      return context.commit('setBreadcrumbInfo', info)
    }
  },
}

export function buildGoalQueryAndResultSetForStatementsByArea(statementsByArea, gradeLevel) {
  const querySet = []
  const resultSet = []

  // Only retrieve goals for areas of need (ignore strengths)
  for (let [subject, statements] of Object.entries(statementsByArea.needs)) {
    const mappedSubjects = returnGoalSubjectForPLWSubject(subject)
    const hasGradeFilter = subjectHasGradeFiltersForAlignedGoals(subject)
    statements.forEach((statement) => {
      const query = {
        search_text: wikiToPlainText(statement.statement_text),
        subjects: mappedSubjects
      }
      if (subject === reading) {
        query.domains = returnGoalDomainsForPLWSkill(statement.skill_name)
      }
      if (hasGradeFilter) {
        query.grade = gradeLevel
      }
      querySet.push(query)

      // Prep result skeleton objects; results are returned in the same order
      const result = {
        subject: subject,
        category: statement.skill_name,
      }
      if (hasGradeFilter) {
        result.grades = [gradeLevel]
      }
      resultSet.push(result)
    })
  }

  return { querySet, resultSet }
}
